import React, { useState } from 'react';
import { database } from '../firebase';
import { ref, push, get, query, orderByChild, equalTo } from 'firebase/database';
import emailjs from '@emailjs/browser';

const AdoptionForm = ({ pet, onClose }) => {
  const [formData, setFormData] = useState({
    nombres: '',
    apellidos: '',
    celular: '',
    email: '',
    dni: '',
    direccion: '',
    ciudad: '',
    distrito: '',
    motivos: ''
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Verificar si ya existe una solicitud con el mismo DNI para esta mascota
  const checkExistingApplication = async (dni) => {
    const adoptionsRef = ref(database, 'adoptions');
    const adoptionsQuery = query(
      adoptionsRef, 
      orderByChild('petId'), 
      equalTo(pet.id)
    );

    const snapshot = await get(adoptionsQuery);
    if (snapshot.exists()) {
      const adoptions = Object.values(snapshot.val());
      return adoptions.some(adoption => adoption.dni === dni);
    }
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      // Validar DNI
      if (formData.dni.length !== 8) {
        setError('El DNI debe tener 8 dígitos');
        setSubmitting(false);
        return;
      }

      // Verificar solicitud existente
      const hasExistingApplication = await checkExistingApplication(formData.dni);
      if (hasExistingApplication) {
        setError('Ya existe una solicitud de adopción con este DNI para esta mascota');
        setSubmitting(false);
        return;
      }

      // Crear la solicitud de adopción
      const adoptionsRef = ref(database, 'adoptions');
      await push(adoptionsRef, {
        ...formData,
        petId: pet.id,
        petName: pet.name,
        status: 'pendiente',
        createdAt: Date.now()
      });

      // Enviar correo electrónico a salvapatasperu@gmail.com
      try {
        await emailjs.send(
          'service_n49mow7', // Reemplazar con tu Service ID de EmailJS
          'template_iuforqk', // Reemplazar con tu Template ID de EmailJS
          {
            to_email: 'salvapatasperu@gmail.com',
            pet_name: pet.name,
            adopter_name: `${formData.nombres} ${formData.apellidos}`,
            adopter_dni: formData.dni,
            adopter_email: formData.email,
            adopter_phone: formData.celular,
            adopter_address: `${formData.direccion}, ${formData.distrito}, ${formData.ciudad}`,
            adoption_reason: formData.motivos,
            date: new Date().toLocaleString('es-ES')
          },
          'sxgHGX5hqIreYMKw8' // Reemplazar con tu Public Key de EmailJS
        );
      } catch (emailError) {
        console.error('Error enviando email:', emailError);
        // No bloquear el flujo si el email falla
      }

      alert('¡Solicitud de adopción enviada exitosamente! Nos pondremos en contacto contigo pronto.');
      onClose();
    } catch (error) {
      setError('Error al enviar la solicitud: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Solicitud de Adopción</h2>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              ×
            </button>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 mb-2">Nombres</label>
                <input
                  type="text"
                  name="nombres"
                  value={formData.nombres}
                  onChange={handleChange}
                  required
                  className="w-full p-2 border rounded focus:ring focus:ring-blue-300"
                />
              </div>

              <div>
                <label className="block text-gray-700 mb-2">Apellidos</label>
                <input
                  type="text"
                  name="apellidos"
                  value={formData.apellidos}
                  onChange={handleChange}
                  required
                  className="w-full p-2 border rounded focus:ring focus:ring-blue-300"
                />
              </div>

              <div>
                <label className="block text-gray-700 mb-2">DNI</label>
                <input
                  type="text"
                  name="dni"
                  value={formData.dni}
                  onChange={handleChange}
                  required
                  pattern="\d{8}"
                  maxLength="8"
                  className="w-full p-2 border rounded focus:ring focus:ring-blue-300"
                />
              </div>

              <div>
                <label className="block text-gray-700 mb-2">Celular</label>
                <input
                  type="tel"
                  name="celular"
                  value={formData.celular}
                  onChange={handleChange}
                  required
                  className="w-full p-2 border rounded focus:ring focus:ring-blue-300"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full p-2 border rounded focus:ring focus:ring-blue-300"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-gray-700 mb-2">Dirección</label>
                <input
                  type="text"
                  name="direccion"
                  value={formData.direccion}
                  onChange={handleChange}
                  required
                  className="w-full p-2 border rounded focus:ring focus:ring-blue-300"
                />
              </div>

              <div>
                <label className="block text-gray-700 mb-2">Ciudad</label>
                <input
                  type="text"
                  name="ciudad"
                  value={formData.ciudad}
                  onChange={handleChange}
                  required
                  className="w-full p-2 border rounded focus:ring focus:ring-blue-300"
                />
              </div>

              <div>
                <label className="block text-gray-700 mb-2">Distrito</label>
                <input
                  type="text"
                  name="distrito"
                  value={formData.distrito}
                  onChange={handleChange}
                  required
                  className="w-full p-2 border rounded focus:ring focus:ring-blue-300"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-gray-700 mb-2">¿Por qué deseas adoptar esta mascota?</label>
                <textarea
                  name="motivos"
                  value={formData.motivos}
                  onChange={handleChange}
                  required
                  rows="4"
                  className="w-full p-2 border rounded focus:ring focus:ring-blue-300"
                ></textarea>
              </div>
            </div>

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-400"
              >
                {submitting ? 'Enviando...' : 'Enviar Solicitud'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdoptionForm;